import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Form } from 'react-final-form';
import { Group } from '../../store/models/Group';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import { TextInputFormField } from '../common/form';
import VerticalForm from '../common/form/VerticalForm';
import styled from '@emotion/styled';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { toJS } from 'mobx';
import { Button, Intent, InputGroup } from '@blueprintjs/core';
import { Link } from 'react-router-dom';
import { TranslatedMessage } from '../common/translation';
import { IconNames } from '@blueprintjs/icons';
import LeaveRouteDirtyWrapper from '../common/LeaveRouteDirtyWrapper';
import { ChromePicker } from 'react-color';
import SecretariatFormField from '../common/form/SecretariatFormField';
import { Secretariat } from '../../store/models';
import { AuthenticationStore, SecretariatStore } from '../../store';

interface GroupFormProps extends WithTranslateProps {
  group?: Instance<typeof Group>;
  groupList?: Array<Instance<typeof Group>>;
  save: (data: any) => void;
  secretariatList?: Array<Instance<typeof Secretariat>>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
}

interface GroupFormState {
  loading: boolean;
  selectedColor: string;
  changeColor?: string;
  color?: string;
}

@inject('AuthenticationStore')
@inject('SecretariatStore')
@observer
class GroupForm extends React.Component<GroupFormProps, GroupFormState> {
  constructor(props: GroupFormProps) {
    super(props);
    this.state = {
      loading: false,
      selectedColor: props.group ? props.group.color : '',
    };
  }

  async componentDidMount() {
    this.setState({ color: this.props.group ? this.props.group.color : '#999' });
  }

  onColorChangeComplete = (event) => {
    this.setState({ color: event.rgb, changeColor: event.hex });
  };

  onAcceptColorChange = (event) => {
    this.setState({ selectedColor: event.hex });
  };

  onCancelColorChange = (event) => {
    const oldcolor = this.props.group!.color;
    this.setState({ color: oldcolor });
    this.onAcceptColorChange(event);
  };

  private getSecretariatId = () => {
    if (this.props.AuthenticationStore!.isSuperAdmin)
      return this.props.SecretariatStore!.selectedSecretariatId === 0
        ? undefined
        : this.props.SecretariatStore!.selectedSecretariatId;
    if (this.props.AuthenticationStore!.isSecretariat) return this.props.AuthenticationStore!.secretariatId;
    return undefined;
  };

  public render() {
    const { group, translate, save, secretariatList } = this.props;
    const currentColor =
      this.props.groupList && group ? '#' + this.props.groupList.filter((grp) => grp.name === group.name)[0].color : '';
    const newColor = this.state.changeColor ? this.state.changeColor : '';
    return (
      <Form
        initialValues={toJS(group) || { secretariatId: this.getSecretariatId() }}
        onSubmit={save}
        render={({ handleSubmit, form, hasValidationErrors, dirty }) => (
          <form onSubmit={handleSubmit}>
            <LeaveRouteDirtyWrapper blockChange={dirty} />
            <Container>
              <VerticalForm labelWidth={175}>
                <TextInputFormField name="name" label={TLCode.GROUP_Name} inline={true} fill={true} required={true} />
                <TextInputFormField
                  name="description"
                  label={TLCode.GROUP_Description}
                  inline={true}
                  fill={true}
                  required={true}
                />
                <SecretariatFormField
                  label={TLCode.SECRETARIAT}
                  name="secretariatId"
                  secretariatList={secretariatList}
                  disabled={!this.props.AuthenticationStore!.isSuperAdmin}
                />
                <DivLine style={{ alignItems: 'left' }}>
                  <br />
                  <b>
                    <TranslatedMessage id={TLCode.GROUP_TargetCounts} />
                  </b>
                  <br />
                  <br />
                </DivLine>
                <DivLine>
                  <DivColumn>
                    <TextInputFormField
                      name="targetDutyCount"
                      label={TLCode.GROUP_TargetDutyCount}
                      inline={true}
                      fill={true}
                      required={true}
                    />
                  </DivColumn>
                  <DivColumn>
                    <TextInputFormField
                      name="targetDayDutyCount"
                      label={TLCode.GROUP_TargetDayDutyCount}
                      inline={true}
                      fill={true}
                      required={true}
                    />
                  </DivColumn>
                  <DivColumn>
                    <TextInputFormField
                      name="targetNightDutyCount"
                      label={TLCode.GROUP_TargetNightDutyCount}
                      inline={true}
                      fill={true}
                      required={true}
                    />
                  </DivColumn>
                </DivLine>
                <DivLine style={{ alignItems: 'left' }}>
                  <br />
                  <b>
                    <TranslatedMessage id={TLCode.GROUP_Display} />
                  </b>
                  <br />
                  <br />
                </DivLine>
                <DivLine>
                  <DivColumn>
                    <DivLine>
                      <DivColumn>
                        <TextInputFormField
                          name="color"
                          label={TLCode.GROUP_Color}
                          inline={true}
                          fill={true}
                          required={true}
                          style={{ background: currentColor }}
                        />
                      </DivColumn>
                      <DivColumn>
                        <InputGroup value={this.state.changeColor} disabled style={{ background: newColor }} />
                      </DivColumn>
                      <DivColumn style={{ marginLeft: '10px'}}>
                          <ButtonsContainer>
                            <Button>
                              <Link to={`${Page.Groups}`}>
                                <TranslatedMessage id={TLCode.BackToList} />
                              </Link>
                            </Button>
                            <Button
                              text={translate(TLCode.GENERAL_Cancel)}
                              icon={IconNames.ERASER}
                              intent={Intent.PRIMARY}
                              onClick={() => form.reset()}
                              disabled={!dirty}
                            />
                            <Button
                              type={'submit'}
                              text={translate(TLCode.GENERAL_Save)}
                              icon={IconNames.FLOPPY_DISK}
                              intent={Intent.PRIMARY}
                              disabled={hasValidationErrors || !dirty}
                            />
                          </ButtonsContainer>
                          </DivColumn>
                    </DivLine>
                    {group && (
                      <>
                        <DivLine>
                          <DivColumn>
                            <ChromePicker color={this.state.color} onChange={this.onColorChangeComplete}></ChromePicker>
                          </DivColumn>
                          <DivColumn style={{ width: 200 }}>
                            <b>Couleurs utilisées</b>
                            {this.props.groupList &&
                              this.props.groupList!.map((grp) => {
                                return (
                                  <>
                                    <div id={grp.name} style={{ background: '#' + grp.color, textAlign: 'center' }}>
                                      {grp.name}
                                    </div>
                                  </>
                                );
                              })}
                          </DivColumn>
 
                        </DivLine>
                      </>
                    )}
                  </DivColumn>
                </DivLine>
              </VerticalForm>
              <br/>
              <ButtonsContainer>
                <Button>
                  <Link to={`${Page.Groups}`}>
                    <TranslatedMessage id={TLCode.BackToList} />
                  </Link>
                </Button>
                <Button
                  text={translate(TLCode.GENERAL_Cancel)}
                  icon={IconNames.ERASER}
                  intent={Intent.PRIMARY}
                  onClick={() => form.reset()}
                  disabled={!dirty}
                />
                <Button
                  type={'submit'}
                  text={translate(TLCode.GENERAL_Save)}
                  icon={IconNames.FLOPPY_DISK}
                  intent={Intent.PRIMARY}
                  disabled={hasValidationErrors || !dirty}
                />
              </ButtonsContainer>
            </Container>
          </form>
        )}
      />
    );
  }
}

export default withTranslate(GroupForm);

const Container = styled.div`
  display: flex;
  width: 70%;
  flex-direction: column;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-content: center;
  justify-content: flex-end;
  & button {
    margin-left: 5px;
  }
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
